import { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useCustomer } from "../../contexts/CustomerContext";
import ChatMessage from "../../components/chat/ChatMessage";
import ChatInput from "../../components/chat/ChatInput";
import ChatHeader from "../../components/chat/ChatHeader";
import { useChatManager } from "../../hooks/useChatManager";

const ChatInterface = () => {
    const user = useAuthUser();
    const { currentCustomer } = useCustomer();
    const messagesEndRef = useRef(null);

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        chatContainer: false,
        inputArea: false
    });

    // Define the initial message for a new chat
    const initialMessages = [
        {
            id: 1,
            type: 'ai',
            content: `Hello ${user?.name}! I'm your AI assistant. How can I help you today?`,
            timestamp: new Date(),
        }
    ];

    // Determine the client name for API calls based on current customer
    const clientName = currentCustomer?.clientId || 'celeste';

    // Debug logging for client selection
    useEffect(() => {
        console.log('ChatInterface: Using client for API calls:', {
            clientName,
            currentCustomer: currentCustomer ? {
                id: currentCustomer.id,
                name: currentCustomer.name,
                clientId: currentCustomer.clientId,
                category: currentCustomer.category
            } : null
        });
    }, [clientName, currentCustomer]);

    // --- All chat logic is now handled by the hook ---
    const {
        messages,
        isTyping,
        handleSendMessage,
        clearChat: handleClearChat // Rename clearChat to handleClearChat for consistency
    } = useChatManager(initialMessages, clientName);

    // Scroll to bottom when new messages are added
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Animation sequence
    useEffect(() => {
        const sequence = [
            { key: 'header', delay: 100 },
            { key: 'chatContainer', delay: 300 },
            { key: 'inputArea', delay: 500 }
        ];

        sequence.forEach(({ key, delay }) => {
            setTimeout(() => {
                setAnimationStates(prev => ({ ...prev, [key]: true }));
            }, delay);
        });
    }, []);
    
    return (
        <div className="responsive-page-layout h-full flex flex-col overflow-hidden">
            {/* Header */}
            <div className={`chat-header-responsive transition-all duration-700 ease-out z-10 ${
                animationStates.header
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 -translate-y-4'
            }`}>
                <ChatHeader
                    user={user}
                    currentCustomer={currentCustomer}
                    onClearChat={handleClearChat}
                />
            </div>

            {/* Chat Messages Container */}
            <div className={`chat-container-responsive flex-1 min-h-0 transition-all duration-700 ease-out ${
                animationStates.chatContainer
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
            }`}>
                <div className="chat-messages-responsive scrollable h-full overflow-y-auto">
                    {messages.map((message) => (
                        <ChatMessage
                            key={message.id}
                            message={message}
                            user={user}
                        />
                    ))}

                    {/* Typing indicator */}
                    {isTyping && (
                        <div className="flex justify-start">
                            <div className="bg-bgSecondary shadow-neo-card-inner p-2 sm:p-3 rounded-md sm:rounded-lg max-w-xs sm:max-w-sm">
                                <div className="flex items-center space-x-1">
                                    <div className="flex space-x-1">
                                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-accent rounded-full animate-bounce"></div>
                                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-accent rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-accent rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                                    </div>
                                    <span className="text-xs sm:text-sm text-font-secondary ml-2">AI is typing...</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input Area */}
            <div className={`chat-input-area-responsive transition-all duration-700 ease-out ${
                animationStates.inputArea
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
            }`}>
                <ChatInput
                    onSendMessage={handleSendMessage}
                    disabled={isTyping}
                />
            </div>
        </div>
    );
};

export default ChatInterface;
